import React from 'react';
import { Box, Button, Flex, Heading, IconButton, Text } from 'pounceV2';
import { FastField, Field, FieldArray, getIn, useFormikContext } from 'formik';
import { FormikTextInputV3 } from 'Components/fields/TextInput/TextInputV3';
import FormikMultiComboboxV3 from 'Components/fields/MultiComboBox/MultiComboBoxV3';
import { SimplePolicyFormValues } from 'Pages/Build/Detections/Policies/SimplePolicyForm/SimplePolicyFormV3';
import { SimpleRuleFormValues } from 'Pages/Build/Detections/Rules/SimpleRuleForm/SimpleRuleFormV3';
import { useTheme } from '@mui/material';

const FrameworkMappingSectionV3 = ({ canEdit = true }: { canEdit: boolean }) => {
  const {
    palette: { tokens },
  } = useTheme();
  const {
    values: { reports },
  } = useFormikContext<SimpleRuleFormValues | SimplePolicyFormValues>();

  return (
    <FieldArray
      name="reports"
      render={({ remove, push, form }) => {
        return (
          <>
            <Flex justifyContent="space-between" flexShrink={1} mb={6} mr={2}>
              <Box>
                <Heading wordBreak="break-word" variant="h5">
                  Framework Mapping
                </Heading>
                <Text variant="body/sm" color={tokens.content.text.neutral[3]}>
                  How this alert gets mapped according to security frameworks, such as MITRE.
                </Text>
              </Box>
              {canEdit ? (
                <Box>
                  <Button
                    variant="secondary"
                    aria-label="Add New"
                    onClick={() => push({ key: '', values: [] })}
                  >
                    Add New
                  </Button>
                </Box>
              ) : null}
            </Flex>

            {reports.map((_, index) => {
              const valuesErr = getIn(form.errors, `reports.${index}.values`);
              const valuesErrAndTouched = !!(valuesErr && getIn(form.touched, `reports.${index}`));

              return (
                <Flex key={index} mb={4} gap={2} align="center">
                  <Box minWidth={300}>
                    <FastField
                      as={FormikTextInputV3}
                      label="Report Key"
                      placeholder="A key for the type of report"
                      name={`reports.${index}.key`}
                      disabled={!canEdit}
                    />
                  </Box>
                  <Box flexGrow={1}>
                    <Field
                      as={FormikMultiComboboxV3}
                      name={`reports.${index}.values`}
                      items={[]}
                      label="Report Values"
                      placeholder="Tags for this report type"
                      invalid={valuesErrAndTouched}
                      disabled={!canEdit}
                      searchable
                      allowAdditions
                    />
                  </Box>
                  {canEdit ? (
                    <Box>
                      <IconButton
                        icon="trash"
                        size="large"
                        variant="ghost"
                        aria-label="Remove report entry"
                        onClick={() => remove(index)}
                      />
                    </Box>
                  ) : null}
                </Flex>
              );
            })}
          </>
        );
      }}
    />
  );
};

export default React.memo(FrameworkMappingSectionV3);
